import CloseIcon from '@mui/icons-material/Close';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { api } from '../../api/client';
import type {
  LiveTvStreamDetail,
  MediaSourceSummary,
  MediaStreamInfo,
  PlaybackDecision,
  StreamPlan,
} from '../../api/types';
import { useApi } from '../../api/useApi';
import { pluginKey } from '../../i18n';
import DecisionReasons from '../DecisionReasons';
import { containerName, describeStream, formatBitrate, rangeName } from './streamText';

/// Why this playback is what it is: the method, the container, the encoder where one runs, and
/// every check that led there. The product's whole point is that this is never hidden, so it
/// is a panel somebody opens rather than something an administrator has to go looking for.
///
/// It takes the decision and what it was made about rather than a session, because a channel
/// is decided exactly like a film and deserves the same answer.
export default function PlaybackDetails({
  decision,
  source,
  liveSessionId,
  onClose,
}: {
  decision: PlaybackDecision;
  source: MediaSourceSummary;
  /// For a channel: the session whose tuner, reception and descrambling are shown as well.
  liveSessionId?: string;
  /// Closes the panel. On a phone it covers the whole screen, and there is nothing beside it
  /// to tap away.
  onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Stack spacing={2} sx={{ p: 3, maxWidth: 560 }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="h3">{t('player.details')}</Typography>
        <IconButton onClick={onClose} aria-label={t('common.close')} edge="end">
          <CloseIcon />
        </IconButton>
      </Stack>

      {liveSessionId && <LiveDetails sessionId={liveSessionId} />}

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }} useFlexGap>
        <Chip color="primary" label={t(`playback.${decision.method}`)} />
        <Chip variant="outlined" label={containerName(decision.container)} />
        {/* The encoder is a description of a piece of hardware, not a word. What identifies it
            to a person is what libavcodec calls it - "h264_videotoolbox" - and which device it
            belongs to, which is the useful half when a machine has two. */}
        {decision.encoder && (
          <Chip
            variant="outlined"
            label={decision.encoder.encoderName}
            title={decision.encoder.deviceId}
          />
        )}
        {decision.estimatedBitrate && (
          <Chip variant="outlined" label={formatBitrate(decision.estimatedBitrate)} />
        )}
        {decision.deinterlace !== 'NotRequired' && (
          <Chip variant="outlined" label={t(`deinterlaceStrategy.${decision.deinterlace}`)} />
        )}
        {decision.deinterlace !== 'NotRequired' && (
          <Chip variant="outlined" label={t(`deinterlaceMode.${decision.deinterlaceMode}`)} />
        )}
        {/* Which of the three happened to the HDR, and on a server mapper also which filter
            did it: a title attribute says that to a mouse and to nobody on a television. */}
        {decision.toneMap !== 'NotRequired' && (
          <Chip
            variant="outlined"
            label={decision.toneMapper
              ? `${t(`toneMapStrategy.${decision.toneMap}`)} · ${decision.toneMapper}`
              : t(`toneMapStrategy.${decision.toneMap}`)}
          />
        )}
      </Stack>

      <Divider />

      <Stack spacing={1}>
        <Typography variant="overline" color="text.secondary">
          {t('player.streams')}
        </Typography>

        {decision.streams.map((plan) => {
          const stream = source.streams.find((candidate) => candidate.index === plan.sourceStreamIndex);

          return (
            <Stack
              key={plan.sourceStreamIndex}
              direction="row"
              spacing={1}
              sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}
              useFlexGap
            >
              <Typography variant="body2" sx={{ minWidth: 72 }} color="text.secondary">
                {t(`streamKind.${plan.kind}`)}
              </Typography>

              <Typography variant="body2">{(stream && describeStream(stream)) || plan.targetCodec}</Typography>

              <Chip
                size="small"
                variant={plan.action === 'Copy' ? 'outlined' : 'filled'}
                color={plan.action === 'Copy' ? 'default' : 'primary'}
                label={`${t(`streamAction.${actionKey(plan)}`)} → ${target(plan, decision, stream)}`}
              />
            </Stack>
          );
        })}
      </Stack>

      <Divider />

      <Stack spacing={1}>
        <Typography variant="overline" color="text.secondary">
          {t('sessions.why')}
        </Typography>

        <DecisionReasons reasons={decision.reasons} />
      </Stack>
    </Stack>
  );
}

/// Where a channel comes from and how it gets here: the source, the device and its input, the
/// signal, and whether and how it is descrambled - whatever the tuner can say. Asked again every
/// couple of seconds while the panel is open, because reception and keys change as one watches.
function LiveDetails({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation();
  const details = useApi(['livetv', 'sessions', sessionId, 'details'], () => api.liveTvSessionDetails(sessionId), {
    staleTime: 0,
    refetchInterval: 2_000,
  });

  if (!details.data) {
    return null;
  }

  const { providerId, viewers, groups } = details.data;
  const label = (key: string) => t([key, pluginKey(providerId, key)], { defaultValue: key });

  return (
    <>
      <Stack spacing={1}>
        <Typography variant="overline" color="text.secondary">
          {t('player.liveSource')}
        </Typography>
        <DetailLine label={t('player.liveProvider')} value={t(pluginKey(providerId, 'plugin.name'), { defaultValue: providerId })} />
        <DetailLine
          label={t('player.liveViewers')}
          value={viewers > 1 ? t('player.liveShared', { count: viewers - 1 }) : t('player.liveAlone')}
        />
      </Stack>

      {groups.map((group) => (
        <Stack key={group.titleKey} spacing={1}>
          <Typography variant="overline" color="text.secondary">
            {label(group.titleKey)}
          </Typography>
          {group.items.map((item) => (
            <DetailLine
              key={item.labelKey}
              label={label(item.labelKey)}
              value={item.valueKey ? t(pluginKey(providerId, item.valueKey), { value: item.value, defaultValue: item.value }) : item.value}
              state={item.state}
            />
          ))}
        </Stack>
      ))}

      <Divider />
    </>
  );
}

const STATE_COLOR: Record<LiveTvStreamDetail['state'], string | undefined> = {
  Neutral: undefined,
  Good: 'success.main',
  Warning: 'warning.main',
  Problem: 'error.main',
};

function DetailLine({ label, value, state = 'Neutral' }: { label: string; value: string; state?: LiveTvStreamDetail['state'] }) {
  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'baseline' }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: STATE_COLOR[state], overflowWrap: 'anywhere' }}>
        {value}
      </Typography>
    </Stack>
  );
}

/// A stream as somebody reads it: what it is, in what language, and how big.
/// What the stream becomes, beside the arrow: the target codec, and for a picture also what
/// changed about it on the way - taken down to eight bit, mapped onto SDR. The source line says
/// where it started; without this the panel says "→ hevc" for a picture that is not the same
/// picture any more.
function target(plan: StreamPlan, decision: PlaybackDecision, stream: MediaStreamInfo | undefined): string {
  const parts = [plan.targetCodec];

  if (plan.kind !== 'Video' || plan.action === 'Copy') {
    return parts.join(' · ');
  }

  if (decision.toneMap === 'ServerHardware') {
    parts.push('SDR');
  } else if (stream && rangeName(stream.range)) {
    // Carried through as it is: HDR stays HDR, and the panel says so rather than leaving it open.
    parts.push(rangeName(stream.range)!);
  }

  if (decision.targetBitDepth && (!stream?.bitDepth || stream.bitDepth > decision.targetBitDepth)) {
    parts.push(`${decision.targetBitDepth}-bit`);
  }

  return parts.join(' · ');
}

/// Sound made again is converted rather than transcoded: it runs on the CPU in a fraction of
/// what the remux around it costs, asks nothing of a GPU, and the server delivers it as a remux.
/// Only a picture is transcoded.
function actionKey(plan: StreamPlan): string {
  return plan.action === 'Transcode' && plan.kind === 'Audio' ? 'Convert' : plan.action;
}
